/**
 * Video Utilities for Gallery & Media Management
 * Handles YouTube IDs, Thumbnails, Embed codes, Vimeo, and direct video file uploads.
 */

export function extractIframeSrc(text: string): string {
  if (!text) return '';
  const match = text.match(/src=["']([^"']+)["']/i);
  return match && match[1] ? match[1] : '';
}

export function isIframeEmbed(text: string): boolean {
  if (!text) return false;
  return text.trim().toLowerCase().includes('<iframe');
}

export function getYouTubeId(urlOrEmbed: string): string | null {
  if (!urlOrEmbed) return null;

  let target = urlOrEmbed.trim();
  if (isIframeEmbed(target)) {
    const extracted = extractIframeSrc(target);
    if (extracted) target = extracted;
  }

  // Handle standard watch URLs, youtu.be, embed, shorts, and nocookie
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/i;
  const match = target.match(regExp);

  if (match && match[2] && match[2].length === 11) {
    return match[2];
  }

  // Check shorts specifically: /shorts/12345678901
  const shortsMatch = target.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/i);
  if (shortsMatch && shortsMatch[1]) {
    return shortsMatch[1];
  }

  return null;
}

export function getYouTubeThumbnail(urlOrEmbed: string): string | null {
  const id = getYouTubeId(urlOrEmbed);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

export function getYouTubeEmbedUrl(urlOrEmbed: string, autoplay = true): string {
  const id = getYouTubeId(urlOrEmbed) || 'ScMzIvxBSi4';
  return `https://www.youtube-nocookie.com/embed/${id}?rel=0${autoplay ? '&autoplay=1' : ''}`;
}

export function getVimeoId(urlOrEmbed: string): string | null {
  if (!urlOrEmbed) return null;
  let target = urlOrEmbed.trim();
  if (isIframeEmbed(target)) {
    const extracted = extractIframeSrc(target);
    if (extracted) target = extracted;
  }
  const match = target.match(/vimeo\.com\/(?:video\/)?([0-9]+)/i);
  return match && match[1] ? match[1] : null;
}

export function isDirectVideoUrl(url: string): boolean {
  if (!url) return false;
  const clean = url.trim().toLowerCase();
  return (
    clean.startsWith('/uploads/') ||
    clean.includes('/uploads/file-') ||
    clean.endsWith('.mp4') ||
    clean.endsWith('.webm') ||
    clean.endsWith('.mov') ||
    clean.endsWith('.mkv') ||
    clean.endsWith('.m4v') ||
    clean.endsWith('.ogv') ||
    clean.includes('.mp4?') ||
    clean.includes('.webm?')
  );
}

export function isVideoItem(item: { type?: string; url?: string; videoEmbed?: string }): boolean {
  if (!item) return false;
  if (item.type === 'youtube' || item.type === 'video') return true;
  if (item.videoEmbed && isIframeEmbed(item.videoEmbed)) return true;
  if (item.url) {
    if (isDirectVideoUrl(item.url)) return true;
    if (isIframeEmbed(item.url)) return true;
    if (Boolean(getYouTubeId(item.url))) return true;
    if (Boolean(getVimeoId(item.url))) return true;
  }
  return false;
}
